import { Routes } from '@angular/router'

import { Action, Resource } from '@ti/contracts'
import { loggedInGuard, notLoggedInGuard } from '@ti/core/auth/guards'
import { checkAccess, MaintenanceGuard, smartRedirect } from '@ti/core/common/guards'
import { allowOnEnv } from '@ti/core/common/guards/allow-on-env'
import { WakeUpProgressStatusProvider } from '@ti/core/common/services'
import { AccessDeniedPageComponent } from '@ti/core/ui-kit/layout/components/access-denied-page'
import { MaintenancePageComponent } from '@ti/core/ui-kit/layout/components/maintenance-page'
import { NotFoundPageComponent } from '@ti/core/ui-kit/layout/components/not-found-page'
import { provideAssistant } from '@ti/shared/assistant/assistant.providers'
import { NewPasswordComponent } from '@ti/shared/auth-ui/components'
import { RoleManagementPageComponent } from '@ti/shared/roles/components'
import { BaseRoleEditModel, BaseRolesListModel } from '@ti/shared/roles/models'

import { TspAdminAuditWrapperComponent } from './audit/components/wrapper/audit-wrapper.component'
import { TspAdminSignInPageComponent } from './common/components/auth/sign-in/sign-in-page.component'
import { TspAdminLayoutComponent } from './common/components/layout/layout.component'
import { mcpAuthorizeGuard } from './mcp/authorize/mcp-authorize.guard'
import { TspAdminUsersPageComponent } from './users/components/page/users-page.component'
import { TspRoleEditModel } from './users/models/role-edit.model'
import { TspRolesListModel } from './users/models/role-list.model'

const APP_ROUTES: Routes = [
  {
    path: '',
    component: TspAdminLayoutComponent,
    canActivate: [loggedInGuard, MaintenanceGuard],
    providers: [WakeUpProgressStatusProvider],
    children: [
      {
        path: 'users',
        loadComponent: () => TspAdminUsersPageComponent,
        canActivate: [checkAccess(Resource.User, Action.View)],
      },
      {
        path: 'roles',
        loadComponent: () => RoleManagementPageComponent,
        canActivate: [checkAccess(Resource.Role, Action.View)],
        providers: [
          { provide: BaseRoleEditModel, useClass: TspRoleEditModel },
          { provide: BaseRolesListModel, useClass: TspRolesListModel },
        ],
      },
      {
        path: 'accounts',
        loadChildren: () => import('./fleet/accounts.routes'),
        canActivate: [checkAccess(Resource.Fleet, Action.View)],
      },
      {
        path: 'devices',
        loadChildren: () => import('./devices/tsp-devices.routes'),
        canActivate: [checkAccess(Resource.Device, Action.View)],
      },
      {
        path: 'billing',
        loadChildren: () => import('./billing/billing.routes'),
        canActivate: [checkAccess(Resource.Billing, Action.View)],
      },
      {
        path: 'white-label',
        loadChildren: () => import('./white-label/white-label.routes'),
        canActivate: [checkAccess(Resource.WhiteLabel, Action.View)],
      },
      {
        path: 'sms',
        loadChildren: () => import('./sms/sms.routes'),
        canActivate: [checkAccess(Resource.SMS, Action.View)],
      },
      {
        path: 'logs',
        loadComponent: () => TspAdminAuditWrapperComponent,
        canActivate: [checkAccess(Resource.AuditLog, Action.View)],
      },
      {
        path: 'developer-api',
        loadChildren: () => import('./developer-api/developer-api.routes'),
        canActivate: [checkAccess(Resource.DeveloperApi, Action.View)],
      },
      // {
      //   path: 'alert-verification',
      //   loadChildren: () => import('./alert-verification/alert-verification.routes'),
      //   canActivate: [checkAccess(Resource.Fleet, Action.View)],
      // },
      {
        path: 'ai-support',
        loadChildren: () => import('@ti/shared/assistant/assistant.routes'),
        providers: [provideAssistant({ routeBase: '/ai-support' })],
      },
      /* Temporarily removed from the application
      { path: 'recycling-bin', component: TspAdminRecyclingBinPageComponent },
      */
      {
        path: '',
        canActivate: [
          smartRedirect(
            {
              users: [Resource.User, Action.View],
              accounts: [Resource.Fleet, Action.View],
              devices: [Resource.Device, Action.View],
              billing: [Resource.Billing, Action.View],
              'white-label': [Resource.WhiteLabel, Action.View],
              sms: [Resource.SMS, Action.View],
              'developer-api': [Resource.DeveloperApi, Action.View],
            },
            'accounts',
          ),
        ],
        pathMatch: 'full',
        component: NotFoundPageComponent,
      },
    ],
  },
  {
    path: 'sign-in',
    canActivate: [notLoggedInGuard, MaintenanceGuard],
    component: TspAdminSignInPageComponent,
  },
  { path: 'change-password', canActivate: [MaintenanceGuard], component: NewPasswordComponent },
  {
    path: 'create-password/:secret',
    canActivate: [MaintenanceGuard],
    component: NewPasswordComponent,
  },
  {
    path: 'reset-password/:secret',
    canActivate: [MaintenanceGuard],
    component: NewPasswordComponent,
  },
  // Outside the layout and deliberately not behind loggedInGuard: see mcpAuthorizeGuard.
  {
    path: 'mcp/authorize',
    canActivate: [mcpAuthorizeGuard],
    loadComponent: () =>
      import('./mcp/authorize/mcp-authorize.page').then((m) => m.McpAuthorizePageComponent),
  },

  {
    path: '___design-system',
    canActivate: [loggedInGuard],
    canMatch: [allowOnEnv(['local', 'dev'])],
    loadComponent: () =>
      import('@ti/core/design-system/design-system.component').then((m) => m.DesignSystemComponent),
  },

  { path: 'access-denied', component: AccessDeniedPageComponent },
  { path: 'maintenance', component: MaintenancePageComponent },
  { path: '**', redirectTo: '' },
]

export default APP_ROUTES
